const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

//the picker offers this many of each, most recent first
const COUNTS = { year: 5, quarter: 8, month: 12, week: 12 };

export const GRANULARITIES = [
  { value: 'year', label: 'Year' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'month', label: 'Month' },
  { value: 'week', label: 'Week' },
  { value: 'custom', label: 'Custom' },
];

const pad = n => n.toString().padStart(2, '0');

const toDateString = date =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

//US Gunite names a week by the Saturday it ends on, so "Week ending September 19" runs from Sunday
//the 13th through Saturday the 19th
const startOfWeek = date => {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  start.setDate(start.getDate() - start.getDay());
  return start;
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

//a range straddling new year carries its own year on both ends, or the start reads as the wrong one
const formatRange = (first, last) =>
  `${MONTHS[first.getMonth()]} ${first.getDate()}${
    first.getFullYear() === last.getFullYear() ? '' : `, ${first.getFullYear()}`
  } – ${MONTHS[last.getMonth()]} ${last.getDate()}, ${last.getFullYear()}`;

//YYYY-MM-DD read as local parts. new Date('2026-08-01') is parsed as UTC and lands on July 31 west
//of Greenwich, which would shift every custom range by a day.
const fromDateString = value => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const buildPeriod = (granularity, first, last, label, isCurrent) => ({
  id: `${granularity}:${toDateString(first)}`,
  //the current period is only part run, and a report over it reads low until the rest is invoiced
  label: isCurrent ? `${label} ${granularity === 'week' ? '(in progress)' : '(to date)'}` : label,
  //"Q3 2026" and "Week ending September 19" do not say what dates they cover, so spell them out
  rangeLabel: formatRange(first, last),
  startDate: toDateString(first),
  endDate: toDateString(last),
  isCurrent,
});

const weekLabel = (end, now) => {
  const label = `Week ending ${MONTHS[end.getMonth()]} ${end.getDate()}`;
  //a week from a previous year needs one, or December reads as this year's
  return end.getFullYear() === now.getFullYear() ? label : `${label}, ${end.getFullYear()}`;
};

//Index 0 is always the period containing today, so it is the one still running.
export const getPeriods = (granularity, now = new Date()) => {
  const periods = [];

  for (let i = 0; i < COUNTS[granularity]; i += 1) {
    if (granularity === 'week') {
      const first = addDays(startOfWeek(now), -7 * i);
      const last = addDays(first, 6);
      periods.push(buildPeriod('week', first, last, weekLabel(last, now), i === 0));
      continue;
    }

    if (granularity === 'month') {
      const first = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const last = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
      const label = `${MONTHS[first.getMonth()]} ${first.getFullYear()}`;
      periods.push(buildPeriod('month', first, last, label, i === 0));
      continue;
    }

    if (granularity === 'quarter') {
      const startMonth = Math.floor(now.getMonth() / 3) * 3 - 3 * i;
      const first = new Date(now.getFullYear(), startMonth, 1);
      const last = new Date(now.getFullYear(), startMonth + 3, 0);
      const label = `Q${Math.floor(first.getMonth() / 3) + 1} ${first.getFullYear()}`;
      periods.push(buildPeriod('quarter', first, last, label, i === 0));
      continue;
    }

    const first = new Date(now.getFullYear() - i, 0, 1);
    const last = new Date(now.getFullYear() - i, 11, 31);
    periods.push(buildPeriod('year', first, last, `${first.getFullYear()}`, i === 0));
  }

  return periods;
};

//the last complete period, so the default report is never a part-run one
export const getDefaultPeriod = (granularity, now = new Date()) => {
  const periods = getPeriods(granularity, now);
  return periods[1] || periods[0];
};

export const isValidCustomRange = (startDate, endDate) =>
  Boolean(startDate) && Boolean(endDate) && startDate <= endDate;

//Custom ranges name no period, so the range itself is the label. The day count is the one thing the
//other granularities make obvious and this does not - a month says how long it is, "March 3 to
//August 19" does not.
export const buildCustomPeriod = (startDate, endDate) => {
  const first = fromDateString(startDate);
  const last = fromDateString(endDate);
  const days = Math.round((last - first) / 86400000) + 1;
  const range = formatRange(first, last);

  return {
    id: `custom:${startDate}:${endDate}`,
    label: range,
    rangeLabel: `${range} · ${days.toLocaleString()} day${days === 1 ? '' : 's'}`,
    startDate,
    endDate,
    isCurrent: false,
  };
};

//A stored report keeps only its dates, so the period it was run for is read back from them. A range
//that is exactly a year, quarter, month or Sunday-to-Saturday week gets that period's label;
//anything else is a custom range. The period counts as current if the run was made before it ended,
//which is what tells a reader the figures were part-run when they were taken.
export const describePeriod = (startDate, endDate, runAt, now = new Date()) => {
  const first = fromDateString(startDate);
  const last = fromDateString(endDate);
  const isCurrent = toDateString(new Date(runAt)) <= endDate;
  const sameDay = (a, b) => toDateString(a) === toDateString(b);

  const year = first.getFullYear();
  const month = first.getMonth();

  if (first.getDate() === 1) {
    if (month === 0 && sameDay(last, new Date(year, 11, 31))) {
      return buildPeriod('year', first, last, `${year}`, isCurrent);
    }
    if (month % 3 === 0 && sameDay(last, new Date(year, month + 3, 0))) {
      return buildPeriod('quarter', first, last, `Q${month / 3 + 1} ${year}`, isCurrent);
    }
    if (sameDay(last, new Date(year, month + 1, 0))) {
      return buildPeriod('month', first, last, `${MONTHS[month]} ${year}`, isCurrent);
    }
  }

  if (first.getDay() === 0 && sameDay(last, addDays(first, 6))) {
    return buildPeriod('week', first, last, weekLabel(last, now), isCurrent);
  }

  return buildCustomPeriod(startDate, endDate);
};
